import { useEffect, useRef, useState } from "react";

import { useModalHistoryEntry } from "../../lib/modalHistory";
import { passwordFieldProps } from "../../lib/inputPrivacy";
import { getOldestReplicaTimestamp } from "../../lib/offline/replica";
import { getAllNeedingPassword, getOutboxCount } from "../../lib/offline/outbox";
import { pullReplica } from "../../lib/offline/offlinePull";
import { drainOutbox, retryNeedsPassword } from "../../lib/offline/offlineSync";
import type { OutboxEntry, PullProgress } from "../../lib/offline/types";

type Props = {
  online: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  showOnlineToast: boolean;
};

export function ConnectionStatusButton({
  online,
  onClick,
}: {
  online: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`topbar__action connection-status ${online ? "online" : "offline"}`}
      data-testid="connection-status"
      title={
        online
          ? "Połączono z internetem"
          : "Tryb offline — dane będą zsynchronizowane po powrocie sieci"
      }
      aria-label={online ? "Status połączenia: online" : "Status połączenia: offline"}
      onClick={onClick}
    >
      <svg
        className="topbar__action-icon"
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {online ? (
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18" />
            <path d="M12 3a14 14 0 0 1 0 18" />
            <path d="M12 3a14 14 0 0 0 0 18" />
          </>
        ) : (
          <>
            <path d="M2 8.82A15 15 0 0 1 12 5c3.1 0 6 .94 8.4 2.55" />
            <path d="M4.93 12.1A10 10 0 0 1 12 10c2.05 0 3.95.62 5.55 1.68" />
            <path d="M8.53 15.42A5 5 0 0 1 12 14.5c.9 0 1.74.24 2.47.66" />
            <path d="M12 19h.01" />
            <path d="m2 2 20 20" />
          </>
        )}
      </svg>
    </button>
  );
}

export function ConnectionStatusModal({ online, open, onClose }: Omit<Props, "onOpen" | "showOnlineToast">) {
  useModalHistoryEntry(open, onClose);

  const [replicaTimestamp, setReplicaTimestamp] = useState<number | null>(null);
  const [outboxCount, setOutboxCount] = useState<number>(0);
  const [passwordEntries, setPasswordEntries] = useState<OutboxEntry[]>([]);
  const [retryPassword, setRetryPassword] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pullProgress, setPullProgress] = useState<PullProgress | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  async function refreshStatus() {
    const ts = await getOldestReplicaTimestamp();
    const count = await getOutboxCount();
    const needing = await getAllNeedingPassword();
    setReplicaTimestamp(ts);
    setOutboxCount(count);
    setPasswordEntries(needing);
  }

  useEffect(() => {
    if (!open) {
      setRetryPassword("");
      setSyncResult(null);
      return;
    }

    void refreshStatus();

    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  async function handlePull() {
    if (!online || pulling) return;

    setPulling(true);
    setPullProgress({ total: 0, completed: 0 });
    setSyncResult(null);

    try {
      const result = await pullReplica((progress) => {
        setPullProgress(progress);
      });

      const ts = await getOldestReplicaTimestamp();
      setReplicaTimestamp(ts);
      await refreshStatus();

      setSyncResult(
        `Pobrano ${result.success}/${result.total} zasobów${result.failed > 0 ? `, ${result.failed} błędów` : ""}.`,
      );
    } catch (error) {
      setSyncResult(`Błąd: ${String(error)}`);
    } finally {
      setPulling(false);
      setPullProgress(null);
    }
  }

  async function handleSync() {
    if (!online || syncing) return;

    setSyncing(true);
    setSyncResult(null);

    try {
      const result = await drainOutbox();
      await refreshStatus();

      const parts: string[] = [];
      if (result.synced > 0) parts.push(`${result.synced} zapisanych`);
      if (result.failed > 0) parts.push(`${result.failed} błędów`);
      if (result.needs_password > 0) parts.push(`${result.needs_password} wymaga hasła`);

      setSyncResult(`Synchronizacja: ${parts.join(", ")}.`);
    } catch (error) {
      setSyncResult(`Błąd: ${String(error)}`);
    } finally {
      setSyncing(false);
    }
  }

  async function handleRetryPassword() {
    if (!online || syncing) return;
    if (!retryPassword.trim()) {
      setSyncResult("Podaj hasło administratora, aby wysłać zapisy oczekujące na hasło.");
      return;
    }

    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await retryNeedsPassword(retryPassword);
      await refreshStatus();
      const parts: string[] = [];
      if (result.synced > 0) parts.push(`${result.synced} zapisanych`);
      if (result.failed > 0) parts.push(`${result.failed} błędów`);
      if (result.needs_password > 0) parts.push(`${result.needs_password} nadal wymaga hasła`);
      setSyncResult(
        parts.length > 0 ? `Hasło: ${parts.join(", ")}.` : "Brak wpisów do ponowienia.",
      );
      if (result.needs_password === 0) {
        setRetryPassword("");
      }
    } catch (error) {
      setSyncResult(`Błąd: ${String(error)}`);
    } finally {
      setSyncing(false);
    }
  }

  const replicaAge = replicaTimestamp ? Date.now() - replicaTimestamp : null;
  const replicaAgeText = replicaAge
    ? replicaAge < 60_000
      ? "< 1 min temu"
      : replicaAge < 3600_000
        ? `${Math.floor(replicaAge / 60_000)} min temu`
        : replicaAge < 86400_000
          ? `${Math.floor(replicaAge / 3600_000)} godz. temu`
          : `${Math.floor(replicaAge / 86400_000)} dni temu`
    : "brak";

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="connection-status-modal-title"
      data-testid="connection-status-modal"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="modal modal--connection" onClick={(event) => event.stopPropagation()}>
        <div
          className="connection-modal__badge"
          data-state={online ? "online" : "offline"}
        >
          <span className="connection-modal__badge-label">{online ? "Online" : "Offline"}</span>
        </div>
        <h3 className="modal__title" id="connection-status-modal-title">
          Status połączenia
        </h3>
        <p className="modal__text">
          {online
            ? "Aplikacja działa w trybie online."
            : "Brak połączenia z internetem. Dane są dostępne z lokalnej kopii. Zmiany zostaną zsynchronizowane po powrocie sieci."}
        </p>

        <div className="modal__section">
          <h4 className="modal__subtitle">Lokalna kopia danych</h4>
          <p className="modal__text modal__text--small">
            Ostatnia aktualizacja: <strong>{replicaAgeText}</strong>
          </p>
          {online && (
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handlePull}
              disabled={pulling}
            >
              {pulling
                ? pullProgress
                  ? `Pobieranie... ${pullProgress.completed}/${pullProgress.total}`
                  : "Pobieranie..."
                : "Synchronizuj dane offline"}
            </button>
          )}
        </div>

        <div className="modal__section">
          <h4 className="modal__subtitle">Kolejka zapisów</h4>
          <p className="modal__text modal__text--small">
            Oczekujących zmian: <strong>{outboxCount}</strong>
          </p>
          {online && outboxCount > 0 && (
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? "Wysyłanie..." : "Wyślij zmiany"}
            </button>
          )}
          {passwordEntries.length > 0 && (
            <div className="connection-modal__retry">
              <p className="modal__text modal__text--small">
                {passwordEntries.length === 1
                  ? "1 zapis wymaga hasła administratora (np. po zamknięciu karty)."
                  : `${passwordEntries.length} zapisy wymagają hasła administratora.`}
              </p>
              <ul className="connection-modal__retry-list">
                {passwordEntries.map((entry) => (
                  <li key={entry.id}>
                    {entry.method} {entry.path}
                  </li>
                ))}
              </ul>
              <label className="connection-modal__retry-label" htmlFor="connection-retry-password">
                Hasło administratora
              </label>
              <input
                id="connection-retry-password"
                className="input"
                type="password"
                value={retryPassword}
                onChange={(event) => setRetryPassword(event.target.value)}
                disabled={!online || syncing}
                data-testid="connection-retry-password"
                {...passwordFieldProps("connection-retry-password")}
              />
              {online && (
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={handleRetryPassword}
                  disabled={syncing || !retryPassword.trim()}
                  data-testid="connection-retry-submit"
                >
                  {syncing ? "Wysyłanie..." : "Wyślij z hasłem"}
                </button>
              )}
            </div>
          )}
        </div>

        {syncResult && (
          <div className="modal__result">
            <p className="modal__text modal__text--small">{syncResult}</p>
          </div>
        )}

        <div className="modal__actions">
          <button type="button" className="btn" data-testid="connection-status-close" onClick={onClose}>
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
}

export function OnlineToast({ show }: { show: boolean }) {
  return (
    <div
      className={`online-notification${show ? " show" : ""}`}
      role="status"
      data-testid="online-notification"
      aria-live="polite"
    >
      <span aria-hidden="true">✅</span>
      <span>Połączono z internetem</span>
    </div>
  );
}

export function useOnlineStatus() {
  const [online, setOnline] = useState(
    () => (typeof navigator !== "undefined" ? navigator.onLine : true),
  );
  const [showToast, setShowToast] = useState(false);
  const wasOffline = useRef(!online);

  useEffect(() => {
    function onOnline() {
      setOnline(true);
      if (wasOffline.current) {
        setShowToast(true);
        window.setTimeout(() => setShowToast(false), 3000);
      }
      wasOffline.current = false;
    }
    function onOffline() {
      wasOffline.current = true;
      setOnline(false);
    }
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  return { online, showToast };
}
